import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Container,
  Paper,
  Grid,
  CircularProgress,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  alpha
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { decryptAppId, decryptText, encryptAppId, encryptText } from '../../../utils/encryption';
import CalibrationLaboratoryService from '../../../services/calibrationlaboratoryService';

// Modern color palette
const colors = {
  primary: {
    main: "#6366f1",
    light: "#818cf8",
    dark: "#4f46e5",
  },
  success: {
    main: "#10b981",
    light: "#34d399",
    dark: "#059669",
  },
  error: {
    main: "#ef4444",
    light: "#f87171",
    dark: "#dc2626",
  },
  grey: {
    50: "#f9fafb",
    100: "#f3f4f6",
    200: "#e5e7eb",
    300: "#d1d5db",
    500: "#6b7280",
  },
};

const FacilityLocationApproval = () => {
  const [loading, setLoading] = useState(true);
  const [facilityLocations, setFacilityLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openDisapproveDialog, setOpenDisapproveDialog] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Password verification states
  const [password, setPassword] = useState('');
  const [passwordVerifying, setPasswordVerifying] = useState(false);
  const [actionType, setActionType] = useState(null); // 'approve' or 'disapprove'

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get encrypted parameters from URL
  const encryptedAppId = searchParams.get('appId');
  const encryptedOrgId = searchParams.get('orgId');
  const encryptedLabTypeId = searchParams.get('labTypeId');

  // Decrypt parameters
  const appId = encryptedAppId ? decryptAppId(encryptedAppId) : null;
  const orgId = encryptedOrgId ? decryptText(encryptedOrgId) : null;
  const labTypeId = encryptedLabTypeId ? decryptText(encryptedLabTypeId) : null;

  // Load facility locations pending approval
  const loadFacilityLocations = useCallback(async () => {
    if (!appId || !orgId) {
      toast.error('Invalid parameters. Please navigate from dashboard.');
      navigate('/main/do/dashboard');
      return;
    }

    try {
      setLoading(true);
      const response = await CalibrationLaboratoryService.getFacilityLocations(appId, orgId);
      
      if (response.success && response.data) {
        const pendingLocations = response.data.filter(loc => loc.isLabSend === 1);
        setFacilityLocations(pendingLocations);
      }
    } catch (error) {
      console.error('Error loading facility locations:', error);
      toast.error('Failed to load facility locations');
    } finally {
      setLoading(false);
    }
  }, [appId, orgId, navigate]);

  useEffect(() => {
    loadFacilityLocations();
  }, [loadFacilityLocations]);

  // Handle back navigation
  const handleBackToDashboard = () => {
    navigate(-1);
  };

  // Helper function to get facility type display name
  const getFacilityTypeDisplayName = (facilityType) => {
    const typeMap = {
      'SiteFacility': 'Site Facility',
      'MobileFacility': 'Mobile Facility',
      'permanentSiteFacility': 'Permanent Site Facility',
      'PermanentFacility': 'Permanent Facility'
    };
    return typeMap[facilityType] || facilityType;
  };

  // Open approve dialog
  const handleOpenApproveDialog = (location) => {
    setSelectedLocation(location);
    setRemarks('');
    setOpenApproveDialog(true);
  };

  // Open disapprove dialog
  const handleOpenDisapproveDialog = (location) => {
    setSelectedLocation(location);
    setRemarks('');
    setOpenDisapproveDialog(true);
  };

  // Close dialogs
  const handleCloseDialogs = () => {
    setOpenApproveDialog(false);
    setOpenDisapproveDialog(false);
    setSelectedLocation(null);
    setRemarks('');
    setPassword('');
    setActionType(null);
  };

  // Handle approve - verify password first
  const handleApprove = async () => {
    if (!selectedLocation) return;

    if (!password.trim()) {
      toast.error('Please enter your password');
      return;
    }

    try {
      setPasswordVerifying(true);

      // Verify password
      const passwordResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/verify-password`,
        {
          orgId: localStorage.getItem('orgId'),
          password: password,
        }
      );

      if (!passwordResponse.data.success) {
        toast.error('Invalid password');
        return;
      }

      // Password verified, proceed with approval
      setProcessing(true);
      const insertedBy = localStorage.getItem('orgId');
      
      const response = await CalibrationLaboratoryService.approveFacilityLocation(
        appId,
        orgId,
        selectedLocation.Id,
        1, // isApprove = 1 (approved)
        insertedBy
      );

      if (response.success) {
        toast.success('Facility location accepted successfully!');
        
        // Send email notification to Lab
        try {
          const emailResponse = await axios.post(
            `${process.env.REACT_APP_API_URL}/assessment-mail/send-approval-notification`,
            {
              appId: appId,
              orgId: orgId,
              recipientType: 'LAB',
              facilityName: `${getFacilityTypeDisplayName(selectedLocation.FacilityType)} - ${selectedLocation.LocationName || 'N/A'}`
            }
          );
          console.log('Email sent to Lab:', emailResponse.data);
        } catch (emailError) {
          console.error('Failed to send email to Lab:', emailError.message);
          // Don't fail the main operation if email fails
        }
        
        handleCloseDialogs();
        await loadFacilityLocations(); // Reload data
      } else {
        toast.error(response.message || 'Failed to accept facility location');
      }
    } catch (error) {
      console.error('Error accepting facility location:', error);
      toast.error(error.message || 'Failed to accept facility location');
    } finally {
      setPasswordVerifying(false);
      setProcessing(false);
    }
  };

  // Handle disapprove - verify password first
  const handleDisapprove = async () => {
    if (!selectedLocation) return;

    if (!remarks.trim()) {
      toast.error('Please provide remarks for disapproval');
      return;
    }

    if (!password.trim()) {
      toast.error('Please enter your password');
      return;
    }

    try {
      setPasswordVerifying(true);

      // Verify password
      const passwordResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/verify-password`,
        {
          orgId: localStorage.getItem('orgId'),
          password: password,
        }
      );

      if (!passwordResponse.data.success) {
        toast.error('Invalid password');
        return;
      }

      // Password verified, proceed with disapproval
      setProcessing(true);
      const insertedBy = localStorage.getItem('orgId');
      
      const response = await CalibrationLaboratoryService.approveFacilityLocation(
        appId,
        orgId,
        selectedLocation.Id,
        2, // isApprove = 2 (disapproved)
        insertedBy,
        remarks
      );

      if (response.success) {
        toast.success('Facility location rejected successfully!');
        
        // Send rejection email notification to Lab
        try {
          const emailResponse = await axios.post(
            `${process.env.REACT_APP_API_URL}/assessment-mail/send-approval-notification`,
            {
              appId: appId,
              orgId: orgId,
              recipientType: 'REJECT',
              facilityName: `${getFacilityTypeDisplayName(selectedLocation.FacilityType)} - ${selectedLocation.LocationName || 'N/A'}`
            }
          );
          console.log('Rejection email sent to Lab:', emailResponse.data);
        } catch (emailError) {
          console.error('Failed to send rejection email to Lab:', emailError.message);
          // Don't fail the main operation if email fails
        }
        
        handleCloseDialogs();
        await loadFacilityLocations(); // Reload data
      } else {
        toast.error(response.message || 'Failed to reject facility location');
      }
    } catch (error) {
      console.error('Error rejecting facility location:', error);
      toast.error(error.message || 'Failed to reject facility location');
    } finally {
      setPasswordVerifying(false);
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading Facility Locations...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 2,
        bgcolor: '#f5f5f5',
      }}
    >
      <Container maxWidth="xl">
        {/* Breadcrumbs */}
        <Box sx={{ mb: 3 }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link
              underline="hover"
              color="inherit"
              onClick={handleBackToDashboard}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Dashboard
            </Link>
            <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
              <BusinessIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Facility Location Acceptance
            </Typography>
          </Breadcrumbs>
        </Box>

        {/* Header */}
        <Paper
          elevation={3}
          sx={{
            mb: 4,
            p: 3,
            background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.primary.dark})`,
            color: 'white',
            borderRadius: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Facility Location Acceptance
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Review and accept facility locations submitted by laboratories
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToDashboard}
              sx={{
                borderColor: 'white',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              Back to Dashboard
            </Button>
          </Box>
        </Paper>

        {/* Main Content */}
        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            {facilityLocations.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <LocationIcon sx={{ fontSize: 64, color: colors.grey[300], mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No facility locations pending acceptance
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  All facility locations have been processed
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: 16 }}>Facility Type</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 16 }}>Location Name</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 16 }}>Address</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 16 }}>Coordinates</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 16 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 16, textAlign: 'center' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {facilityLocations.map((location, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:hover': {
                            backgroundColor: colors.grey[50],
                          },
                        }}
                      >
                        <TableCell>
                          <Chip
                            label={getFacilityTypeDisplayName(location.FacilityType)}
                            color="primary"
                            variant="outlined"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{location.LocationName || 'N/A'}</TableCell>
                        <TableCell sx={{ maxWidth: 300 }}>
                          {location.Address || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            Lat: {location.Latitude ? parseFloat(location.Latitude).toFixed(6) : 'N/A'}
                          </Typography>
                          <Typography variant="body2">
                            Lng: {location.Longitude ? parseFloat(location.Longitude).toFixed(6) : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {location.isLabSend === 1 && (location.isApprove === null || location.isApprove === 0) ? (
                          <Chip
                            label="Pending"
                            color="warning"
                            size="small"
                          />
                          ) : location.isApprove === 1 ? (
                          <Chip
                            label="Accepted"
                            color="success"
                            size="small"
                          />
                          ) : location.isApprove === 2 ? (
                          <Chip
                            label="Rejected"
                            color="error"
                            size="small"
                          />
                          ) : null}
                        </TableCell>
                        <TableCell>
                          { location.isLabSend === 1 && (location.isApprove === null || location.isApprove === 0) && (
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => handleOpenApproveDialog(location)}
                              sx={{
                                background: `linear-gradient(135deg, ${colors.success.main}, ${colors.success.dark})`,
                                '&:hover': {
                                  background: `linear-gradient(135deg, ${colors.success.dark}, ${colors.success.main})`,
                                },
                              }}
                            >
                              Accept
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<CancelIcon />}
                              onClick={() => handleOpenDisapproveDialog(location)}
                              sx={{
                                background: `linear-gradient(135deg, ${colors.error.main}, ${colors.error.dark})`,
                                '&:hover': {
                                  background: `linear-gradient(135deg, ${colors.error.dark}, ${colors.error.main})`,
                                },
                              }}
                            >
                              Reject
                            </Button>
                          </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Container>

      {/* Approve Dialog */}
      <Dialog
        open={openApproveDialog}
        onClose={handleCloseDialogs}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: colors.success.main }}>
          Accept Facility Location
        </DialogTitle>
        <DialogContent>
          {selectedLocation && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                You are accepting the facility location for{' '}
                <strong>{getFacilityTypeDisplayName(selectedLocation.FacilityType)}</strong>
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Password (Required)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !processing && !passwordVerifying) {
                        handleApprove();
                      }
                    }}
                    placeholder="Enter your password to accept"
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseDialogs} disabled={processing || passwordVerifying}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApprove}
            disabled={processing || passwordVerifying || !password.trim()}
            startIcon={
              (processing || passwordVerifying) ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            sx={{
              background: `linear-gradient(135deg, ${colors.success.main}, ${colors.success.dark})`,
            }}
          >
            {passwordVerifying ? 'Verifying...' : processing ? 'Processing...' : 'Confirm Accept'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disapprove Dialog */}
      <Dialog
        open={openDisapproveDialog}
        onClose={handleCloseDialogs}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: colors.error.main }}>
          Reject Facility Location
        </DialogTitle>
        <DialogContent>
          {selectedLocation && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                You are rejecting the facility location for{' '}
                <strong>{getFacilityTypeDisplayName(selectedLocation.FacilityType)}</strong>
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Remarks (Required)"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Please provide reason for rejection"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Password (Required)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !processing && !passwordVerifying && remarks.trim()) {
                        handleDisapprove();
                      }
                    }}
                    placeholder="Enter your password to reject"
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseDialogs} disabled={processing || passwordVerifying}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDisapprove}
            disabled={processing || passwordVerifying || !remarks.trim() || !password.trim()}
            startIcon={
              (processing || passwordVerifying) ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            sx={{
              background: `linear-gradient(135deg, ${colors.error.main}, ${colors.error.dark})`,
            }}
          >
            {passwordVerifying ? 'Verifying...' : processing ? 'Processing...' : 'Confirm Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FacilityLocationApproval;
